import * as B from '@blueprintjs/core';
import queryString from 'query-string';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {ApiNote} from 'app/modules/Remote/Feature';
import {useAuth, useUserInfo} from 'app/providers/AuthProvider';
import {isUpstreamStaff} from 'app/utils/featureFlags';
import {useStateWithDeps} from 'app/utils/hookUtils';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import * as routeUtils from 'app/utils/routeUtils';

import cs from './AgentExplorer.module.scss';
import AgentExplorerSplash from './AgentExplorerSplash';
import {FewShotExemplarFlow, RunExemplarVisibility, RunExemplars} from './fewShotExemplarTypes';
import {useExploreRunsQuery} from './hooks';
import PromptView, {Tab} from './PromptView';
import RunView from './RunView';
import {ApiExploreRun} from './types';

interface AgentExplorerProps {
  featureId: number;
  imageRefs: mapUtils.MapImageRefs;
  focusedNoteId: number | string | null;
  onActiveRunNotesChange: (notes: ApiNote[], runId: number | null) => void;
  onActiveRunExemplarsChange: (exemplars: RunExemplars | null) => void;
  runExemplarVisibility: RunExemplarVisibility;
  onRunExemplarVisibilityChange: (visibility: RunExemplarVisibility) => void;
  onNoteClick: (note: ApiNote) => void;
  onShowAllObservations: () => void;
  onClearFocus: () => void;
  setImageRefs: (imageRefs: mapUtils.MapImageRefs) => void;
  onEnterCompareMode: () => void;
  onExitCompareMode: () => void;
  fewShotFlow: FewShotExemplarFlow;
}

const AgentExplorer: React.FC<AgentExplorerProps> = ({
  featureId,
  imageRefs,
  focusedNoteId,
  onActiveRunNotesChange,
  onActiveRunExemplarsChange,
  runExemplarVisibility,
  onRunExemplarVisibilityChange,
  onNoteClick,
  onShowAllObservations,
  onClearFocus,
  setImageRefs,
  onEnterCompareMode,
  onExitCompareMode,
  fewShotFlow,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // The selected run lives in the URL (?agentRunId=<id>) so runs are
  // deep-linkable. The param is the source of truth: reads come straight from
  // the location and writes replace the current history entry, mirroring how
  // useSceneUrlState manages the scene params.
  const queryParams = useMemo(() => queryString.parse(location.search), [location.search]);
  const runIdParam = routeUtils.getStringParamFromQueryParams(
    queryParams,
    routeUtils.AGENT_RUN_ID_QUERY_PARAM
  );
  const parsedRunId = runIdParam ? parseInt(runIdParam, 10) : NaN;
  const selectedRunId = Number.isFinite(parsedRunId) ? parsedRunId : null;

  const setSelectedRunId = useCallback(
    (runId: number | null) => {
      const nextParams = {...queryParams};
      if (runId === null) {
        delete nextParams[routeUtils.AGENT_RUN_ID_QUERY_PARAM];
      } else {
        nextParams[routeUtils.AGENT_RUN_ID_QUERY_PARAM] = String(runId);
      }
      const search = queryString.stringify(nextParams);
      navigate(`${location.pathname}${search ? `?${search}` : ''}${location.hash}`, {
        replace: true,
      });
    },
    [queryParams, location.pathname, location.hash, navigate]
  );

  const [promptTab, setPromptTab] = useStateWithDeps<Tab>('prompt', [featureId]);
  const {data: runs} = useExploreRunsQuery(featureId);

  const setImageRefsFromRun = useCallback(
    (run: ApiExploreRun) => {
      // NOTE: For now this assumes that there will only be 1 or 2 scenes per run, which is true for the current agent.
      // If that changes we will need to revisit how we handle this.
      const derivedRefs = run.scenes.map((scene) => ({
        layerKey: layerUtils.getLayerKeyFromSourceAndLayer(scene.sourceId, scene.layerId),
        cursor: scene.sensingTime,
      })) as mapUtils.MapImageRefs;
      // failed runs may not have any scenes-- don't overwrite current imageRefs in that case
      if (derivedRefs.length > 0) {
        setImageRefs(derivedRefs);
      }
    },
    [setImageRefs]
  );

  // URL → map state: when the run id was present on mount (a deep link), show
  // the run's scenes once runs load. Click-selection sets the refs directly in
  // onSelectRun, so this only consumes the initial value.
  const initialRunIdRef = useRef(selectedRunId);
  useEffect(() => {
    if (!initialRunIdRef.current || !runs) {
      return;
    }
    const run = runs.find((r) => r.id === initialRunIdRef.current);
    initialRunIdRef.current = null;
    if (run) {
      setImageRefsFromRun(run);
    }
  }, [runs, setImageRefsFromRun]);

  const auth = useAuth();
  const [org, profile] = useUserInfo();
  const [dismissedSplash, setDismissedSplash] = useState(false);

  const isStaff = isUpstreamStaff(profile);

  const showSplash = React.useMemo(
    () =>
      !isStaff &&
      (!org.getIn(['settings', 'agent_settings', 'enabled']) ||
        (!profile.getIn(['settings', 'hasSeenAgentSplash']) && !dismissedSplash)),
    [isStaff, org, profile, dismissedSplash]
  );

  const onDismissSplash = () => {
    setDismissedSplash(true);
    if (auth.status === 'logged in') {
      auth.actions.updateSettings({hasSeenAgentSplash: true});
    }
  };

  // Deep-link / focus → show the run that owns the focused note. The URL only
  // carries the note id.
  const focusedRun = useMemo(
    () =>
      focusedNoteId && runs
        ? runs.find((r) => r.notes.some((n) => n.id === focusedNoteId))
        : undefined,
    [focusedNoteId, runs]
  );

  const activeRunId = selectedRunId ?? focusedRun?.id ?? null;

  const isResolvingFocusedNote = !!focusedNoteId && selectedRunId === null && !runs;

  const showHistoryTab = !!runs?.length;

  return (
    <div className={cs.content}>
      {isResolvingFocusedNote ? (
        <div className={cs.loading}>
          <B.Spinner />
        </div>
      ) : showSplash && !activeRunId ? (
        <AgentExplorerSplash onGetStarted={onDismissSplash} />
      ) : activeRunId ? (
        <RunView
          featureId={featureId}
          runId={activeRunId}
          focusedNoteId={focusedNoteId}
          onBack={() => {
            setSelectedRunId(null);
            onClearFocus();
            setPromptTab('history');
          }}
          onActiveRunNotesChange={onActiveRunNotesChange}
          onActiveRunExemplarsChange={onActiveRunExemplarsChange}
          runExemplarVisibility={runExemplarVisibility}
          onRunExemplarVisibilityChange={onRunExemplarVisibilityChange}
          onNoteClick={onNoteClick}
          onShowAllObservations={onShowAllObservations}
          onClearFocus={onClearFocus}
        />
      ) : (
        <PromptView
          featureId={featureId}
          onSelectRun={(run) => {
            setSelectedRunId(run.id);
            setImageRefsFromRun(run);
          }}
          imageRefs={imageRefs}
          activeTab={showHistoryTab ? promptTab : 'prompt'}
          onActiveTabChange={setPromptTab}
          showHistoryTab={showHistoryTab}
          onEnterCompareMode={onEnterCompareMode}
          onExitCompareMode={onExitCompareMode}
          fewShotFlow={fewShotFlow}
        />
      )}
    </div>
  );
};

export default AgentExplorer;
